import { ActivityEnrichment } from './types';

/**
 * IMBEE-8954 — the client fingerprint, read once per page load (TDS §6f-v(a)).
 *
 * Coarse by intent: device class, browser/OS family and version, language. No fingerprinting
 * surface beyond what a server sees in `User-Agent` anyway, and no page CONTENT is ever read.
 * `ip`/`city`/`country` are deliberately NOT sent — the service derives the IP from
 * `x-forwarded-for` and `resolvedIp = ip ?? dto.ip` gives the server value precedence, so a
 * client-supplied one is at best redundant.
 */

const uaMatch = (ua: string, re: RegExp): string | undefined => {
  const m = ua.match(re);
  return m?.[1];
};

export const parseUserAgent = (ua: string): Pick<ActivityEnrichment, 'browser' | 'browserVersion' | 'os' | 'osVersion'> => {
  // Order matters: Edge and Opera both carry "Chrome", Chrome carries "Safari".
  let browser: string | undefined;
  let browserVersion: string | undefined;
  if (/Edg\//.test(ua)) {
    browser = 'Edge';
    browserVersion = uaMatch(ua, /Edg\/(\d+)/);
  } else if (/OPR\//.test(ua)) {
    browser = 'Opera';
    browserVersion = uaMatch(ua, /OPR\/(\d+)/);
  } else if (/Firefox\//.test(ua)) {
    browser = 'Firefox';
    browserVersion = uaMatch(ua, /Firefox\/(\d+)/);
  } else if (/Chrome\//.test(ua)) {
    browser = 'Chrome';
    browserVersion = uaMatch(ua, /Chrome\/(\d+)/);
  } else if (/Safari\//.test(ua)) {
    browser = 'Safari';
    browserVersion = uaMatch(ua, /Version\/(\d+)/);
  }

  let os: string | undefined;
  let osVersion: string | undefined;
  if (/Windows NT/.test(ua)) {
    os = 'Windows';
    osVersion = uaMatch(ua, /Windows NT ([\d.]+)/);
  } else if (/Android/.test(ua)) {
    os = 'Android';
    osVersion = uaMatch(ua, /Android ([\d.]+)/);
  } else if (/(iPhone|iPad|iPod)/.test(ua)) {
    os = 'iOS';
    osVersion = uaMatch(ua, /OS ([\d_]+)/)?.replace(/_/g, '.');
  } else if (/Mac OS X/.test(ua)) {
    os = 'macOS';
    osVersion = uaMatch(ua, /Mac OS X ([\d_.]+)/)?.replace(/_/g, '.');
  } else if (/Linux/.test(ua)) {
    os = 'Linux';
  }

  return { browser, browserVersion, os, osVersion };
};

export const parseUtm = (search: string): Pick<ActivityEnrichment, 'utmSource' | 'utmMedium' | 'utmCampaign'> => {
  try {
    const p = new URLSearchParams(search);
    return {
      utmSource: p.get('utm_source') || undefined,
      utmMedium: p.get('utm_medium') || undefined,
      utmCampaign: p.get('utm_campaign') || undefined,
    };
  } catch {
    return {};
  }
};

/**
 * `pageUrl` is the CUSTOMER's page (from `IMBEE_PAGE_CONTEXT`), not the iframe's own location — the
 * UTM parameters live on the host page, and the iframe's URL carries only widget plumbing.
 */
export const collectEnrichment = (pageUrl: string, referrer: string): ActivityEnrichment => {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent || '';
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/.test(ua);
  let search = '';
  try {
    search = new URL(pageUrl).search;
  } catch {
    /* a malformed parent URL must not break the beacon */
  }

  return {
    device: isMobile ? 'mobile' : 'desktop',
    isMobile,
    language: typeof navigator === 'undefined' ? undefined : navigator.language,
    ...(referrer ? { referrer } : {}),
    ...parseUserAgent(ua),
    ...parseUtm(search),
  };
};
